import { useQuery, useMutation } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { usePortalActiveClinic } from '@/lib/portalActiveClinic';

export interface PortalDocumentItem {
  id: string;
  clinic_id: string;
  clinic_name: string;
  filename: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  tags: string[];
  notes: string | null;
  created_at: string;
}

export const portalDocumentsQueryKey = (patientId: string | null) =>
  ['portal', 'documents', patientId] as const;

export function usePortalDocuments() {
  const { activePatientId, isLoading: clinicsLoading } = usePortalActiveClinic();

  const query = useQuery({
    queryKey: portalDocumentsQueryKey(activePatientId),
    enabled: Boolean(activePatientId),
    queryFn: async (): Promise<PortalDocumentItem[]> => {
      if (!activePatientId) return [];

      const { data, error } = await supabase
        .from('documents')
        .select(
          `id, clinic_id, filename, original_filename, mime_type, size_bytes, 
           tags, notes, created_at,
           clinic:clinics!documents_clinic_id_fkey(name)`
        )
        .eq('patient_id', activePatientId)
        .is('archived_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;

      type Joined = {
        id: string;
        clinic_id: string;
        filename: string;
        original_filename: string;
        mime_type: string;
        size_bytes: number;
        tags: string[];
        notes: string | null;
        created_at: string;
        clinic: { name: string } | null;
      };

      return ((data ?? []) as unknown as Joined[]).map(
        (r): PortalDocumentItem => ({
          id: r.id,
          clinic_id: r.clinic_id,
          clinic_name: r.clinic?.name ?? '—',
          filename: r.filename,
          original_filename: r.original_filename,
          mime_type: r.mime_type,
          size_bytes: r.size_bytes,
          tags: r.tags ?? [],
          notes: r.notes,
          created_at: r.created_at,
        })
      );
    },
    staleTime: 15_000,
  });

  return {
    ...query,
    isLoading: clinicsLoading || query.isLoading,
  };
}

export function usePortalDownloadDocument() {
  return useMutation({
    mutationFn: async (documentId: string): Promise<string> => {
      const { data, error } = await supabase.rpc('get_document_download_url', {
        p_document_id: documentId,
      });
      if (error) throw error;

      const result = data as unknown as { storage_path: string; expires_at: string };

      const { data: signedData, error: signedError } = await supabase.storage
        .from('patient-documents')
        .createSignedUrl(result.storage_path, 60);

      if (signedError) throw signedError;
      if (!signedData?.signedUrl) throw new Error('Could not generate download URL');

      return signedData.signedUrl;
    },
  });
}
